import clsx from 'clsx';
import type { IDataTablePageSizeSelectBoxOptions, IDataTablePageSizeSelectBoxSharedProps, IDataTablePaginationComponentCommonProps } from '../shared/types';

/**
 * Varsayılan sayfa boyutu seçenekleri.
 * Kullanıcı options prop'u vermezse bu değerler kullanılır.
 */
const DEFAULT_OPTIONS: IDataTablePageSizeSelectBoxOptions[] = [
  { value: 10, label: '10' },
  { value: 20, label: '20' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
];

/**
 * DataTablePageSizeSelectBox component'inin props interface'i.
 */
export interface IDataTablePageSizeSelectBoxProps extends IDataTablePageSizeSelectBoxSharedProps, IDataTablePaginationComponentCommonProps {}

/**
 * DataTablePageSizeSelectBox Component
 *
 * Kullanıcının sayfa başına gösterilecek kayıt sayısını seçmesini sağlar.
 * Native HTML select elementi kullanır ve Select2 ile entegre çalışır.
 *
 * Özellikler:
 * - Sayfa boyutu seçimi (10, 20, 50, 100 gibi)
 * - Seçim yapıldığında otomatik olarak ilk sayfaya döner (pageIndex: 0)
 * - Select2 entegrasyonu (data-control="select2" attribute'u ile)
 *
 * Varsayılan davranış:
 * - options: [10, 20, 50, 100]
 * - Sayfa boyutu değiştiğinde pageIndex otomatik olarak 0'a sıfırlanır
 *
 * @param props - Page size select box props'ları
 * @param props.setPagination - Pagination durumunu güncelleme fonksiyonu
 * @param props.paginationState - Mevcut pagination durumu (pageSize değerini okumak için)
 * @param props.paginationMeta - Pagination meta bilgileri (pageCount kontrolü için)
 * @param props.options - Gösterilecek sayfa boyutu seçenekleri. Varsayılan: [10, 20, 50, 100]
 *                       Her seçenek { value: number, label: string } formatında olmalı.
 * @param props.wrapperClassName - Select wrapper div'i için CSS sınıfı
 * @param props.selectClassName - Select elementi için CSS sınıfı (varsayılan: 'form-select')
 *
 * @returns JSX.Element | null - Select box UI'ı veya null (eğer pageCount < 1 ise)
 *
 * @example
 * ```tsx
 * <DataTablePageSizeSelectBox
 *   options={[
 *     { value: 5, label: '5' },
 *     { value: 10, label: '10' },
 *     { value: 25, label: '25' }
 *   ]}
 *   wrapperClassName="custom-wrapper"
 *   selectClassName="custom-select"
 * />
 * ```
 */
export default function DataTablePageSizeSelectBox(props: IDataTablePageSizeSelectBoxProps) {
  const {
    // Core
    setPagination,
    paginationState,
    paginationMeta,

    // Options
    options = DEFAULT_OPTIONS,

    // Other Customizable Props
    wrapperClassName,
    selectClassName,
  } = props;

  if (paginationMeta?.pageCount < 1) return null;
  return (
    <div className={clsx('d-flex justify-content-end', wrapperClassName)}>
      <select
        value={paginationState.pageSize}
        name="pageSize"
        onChange={(e) => {
          setPagination((prev) => ({
            ...prev,
            pageSize: Number(e.target.value),
            pageIndex: 0,
          }));
        }}
        data-control="select2"
        data-hide-search="true"
        className={clsx('form-select', selectClassName)}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}
